/*
 * Copyright 2018 Google LLC
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *   https://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */

import isMobile from 'is-mobile-with-ipad';

let STATE = {
  UNKNOWN: -1,
  ROTATE: 0,
  MOVE: 1,
  SCALE: 2,
};

let tempCurrentTouchCenter = new THREE.Vector2();
let tempPreviousTouchCenter = new THREE.Vector2();

let tempCurrentTouchVector = new THREE.Vector2();
let tempPreviousTouchVector = new THREE.Vector2();

let tempWorldPos = new THREE.Vector3();
let tempWorldQuat = new THREE.Quaternion();

let tempPos = new THREE.Vector3();
let tempQuat = new THREE.Quaternion();
let tempScale = new THREE.Vector3();

let tempCameraPos = new THREE.Vector3();
let tempBox = new THREE.Box3();

let tempAxis = new THREE.Vector3(0, 1, 0);

let createTouch = () => {
  return {
    current: new THREE.Vector2(0, 0),
    currentTouch: undefined,
    previous: new THREE.Vector2(0, 0),
    previousTouch: undefined,
  };
};

export default class ARControls extends THREE.EventDispatcher {
  constructor(props) {
    super(props);
    this.updateHit = this.updateHit.bind(this);
    this.onDownPan = this.onDownPan.bind(this);
    this.onDragPan = this.onDragPan.bind(this);
    this.setup(props);
  }

  setup = props => {
    this.xrSession = props.xrSession;
    this.frameOfRef = props.frameOfRef;

    this.model = props.model;
    this.scene = props.scene;
    this.camera = props.camera;
    this.canvas = props.canvas;
    this.renderer = props.renderer;

    this.useHitTest = props.useHitTest != undefined ? props.useHitTest : true;
    this.translationSensitivity = props.translateSensitivity != undefined
      ? props.translateSensitivity
      : 0.5;
    this.rotationSensitivity = props.rotationSensitivity != undefined
      ? props.rotationSensitivity
      : 2.0;

    this.scale = props.scale != undefined ? props.scale : true;
    this.rotate = props.rotate != undefined ? props.rotate : true;
    this.translate = props.translate != undefined ? props.translate : true;

    this.bb = props.boundingBox;
    this.debug = props.debug != undefined ? props.debug : false;

    this.enabled = false;
    this.down = false;
    this.hit = false;
    this.minScale = 0.25;
    this.maxScale = 2.0;

    this.origin = new THREE.Vector3();
    this.quat = new THREE.Quaternion();
    this.zeroQuat = new THREE.Quaternion();

    this.hitTestFailed = 0;
    this.hitOffset = new THREE.Vector3();
    this.hitOffsetNormalized = new THREE.Vector3();
    this.hitMovePoint = new THREE.Vector3();
    this.hitMovePointPrev = new THREE.Vector3();

    this.hitRotatePoint = new THREE.Vector3();
    this.hitRotatePointPrev = new THREE.Vector3();

    this.v0 = new THREE.Vector3();
    this.v1 = new THREE.Vector3();

    this.raycaster = new THREE.Raycaster();

    this.normalizedMouse = new THREE.Vector2();
    this.normalizedMousePrev = new THREE.Vector2();
    this.mouse = new THREE.Vector2();
    this.mousePrev = new THREE.Vector2();

    this.STATE = STATE.UNKNOWN;

    this.proximityWarningPrev = false;
    this.proximityWarning = false;

    this.touchSeparationDistanceDelta = 0.0;
    this.touchMovementDistanceDelta = 0.0;
    this.touchAngleDelta = 0.0;

    this.touchesActive = [];
    this.touchesHash = {};

    this.computeBoundingSphere();
  };

  computeBoundingSphere = () => {
    this.bs = new THREE.Sphere();
    this.bb.getBoundingSphere(this.bs);
    this.size = new THREE.Vector3();
    this.bb.getSize(this.size);

    this.originObject = new THREE.Object3D();

    this.box = new THREE.Mesh(
      new THREE.BoxBufferGeometry(this.size.x, this.size.y, this.size.z),
      new THREE.MeshBasicMaterial({
        color: new THREE.Color(0xff0000),
        wireframe: true,
        transparent: true,
        opacity: 0.75,
        side: THREE.DoubleSide,
      })
    );

    this.originObject.add(this.box);
    this.box.position.set(0, this.bs.center.y, 0);

    this.plane = new THREE.Mesh(
      new THREE.PlaneBufferGeometry(20, 20, 40, 40),
      new THREE.MeshBasicMaterial({
        color: new THREE.Color(0xff00ff),
        wireframe: true,
        transparent: true,
        opacity: 0.25,
      })
    );

    this.plane.geometry.applyMatrix(
      new THREE.Matrix4().makeRotationX(THREE.Math.degToRad(-90))
    );
  };

  update = time => {
    if (this.enabled) {
      if (!this.xrSession || !this.xrSession.requestHitTest) {
        return;
      }

      this.model.getWorldPosition(tempWorldPos);
      this.origin.copy(tempWorldPos);

      this.originObject.position.copy(this.origin);

      this.model.getWorldQuaternion(tempWorldQuat);
      this.originObject.quaternion.copy(tempWorldQuat);
      this.originObject.updateMatrixWorld(true);
    }
  };

  enable = () => {
    this.enabled = true;
    if (this.debug) {
      this.scene.add(this.originObject);
      this.scene.add(this.plane);
    }
    this.originObject.scale.set(1, 1, 1);
    this.model.getWorldPosition(tempWorldPos);
    this.plane.position.copy(tempWorldPos);
    this.plane.updateMatrixWorld(true);
    this.enableEvents();
  };

  enableEvents = () => {
    this.canvas.addEventListener('contextmenu', this.contextmenu, false);
    this.canvas.addEventListener('mousedown', this.mousedown, false);
    this.canvas.addEventListener('mousemove', this.mousemove, false);
    this.canvas.addEventListener('mouseup', this.mouseup, false);
    this.canvas.addEventListener('touchstart', this.touchStart, false);
    this.canvas.addEventListener('touchmove', this.touchMove, false);
    this.canvas.addEventListener('touchend', this.touchEnd, false);
    this.canvas.addEventListener('touchcancel', this.touchEnd, false);
  };

  disable = () => {
    this.enabled = false;
    if (this.debug) {
      this.scene.remove(this.originObject);
      this.scene.remove(this.plane);
    }

    this.model.quaternion.copy(this.zeroQuat);
    this.disableEvents();
  };

  disableEvents = () => {
    this.canvas.removeEventListener('contextmenu', this.contextmenu);
    this.canvas.removeEventListener('mousedown', this.mousedown);
    this.canvas.removeEventListener('mousemove', this.mousemove);
    this.canvas.removeEventListener('mouseup', this.mouseup);
    this.canvas.removeEventListener('touchstart', this.touchStart);
    this.canvas.removeEventListener('touchmove', this.touchMove);
    this.canvas.removeEventListener('touchend', this.touchEnd);
    this.canvas.removeEventListener('touchcancel', this.touchEnd);
  };

  contextmenu = event => {
    event.preventDefault();
  };

  updateMouse = (x, y) => {
    this.normalizedMousePrev.set(
      this.normalizedMouse.x,
      this.normalizedMouse.y
    );
    this.normalizedMouse.x = x / window.innerWidth;
    this.normalizedMouse.y = y / window.innerHeight;

    this.mousePrev.copy(this.mouse);
    this.mouse.x = this.normalizedMouse.x * 2 - 1;
    this.mouse.y = -this.normalizedMouse.y * 2 + 1;
  };

  updateHitOffset = () => {
    this.model.updateMatrixWorld(true);
    this.model.getWorldPosition(tempWorldPos);
    this.hitOffset.copy(tempWorldPos);
    this.hitOffset.project(this.camera);
    this.hitOffset.z = 0.0;

    this.hitOffsetNormalized.copy(this.hitOffset);
    this.hitOffsetNormalized.addScalar(1.0);
    this.hitOffsetNormalized.divideScalar(2.0);
    this.hitOffsetNormalized.y = 1.0 - this.hitOffsetNormalized.y;

    this.hitOffsetNormalized.set(
      this.normalizedMouse.x - this.hitOffsetNormalized.x,
      this.normalizedMouse.y - this.hitOffsetNormalized.y,
      0.0
    );

    this.hitOffset.x = this.mouse.x - this.hitOffset.x;
    this.hitOffset.y = this.mouse.y - this.hitOffset.y;
  };

  async updateHit(point) {
    if (!this.xrSession || !this.xrSession.requestHitTest) {
      return;
    }

    let hits;
    if (this.useHitTest) {
      const hx = (this.normalizedMouse.x - this.hitOffsetNormalized.x) * 2 - 1;
      const hy = (this.normalizedMouse.y - this.hitOffsetNormalized.y) * 2 + 1;

      this.hitcaster = this.hitcaster || new THREE.Raycaster();
      this.hitcaster.setFromCamera({ x: hx, y: hy }, this.camera);
      const ray = this.hitcaster.ray;

      const origin = new Float32Array(ray.origin.toArray());
      const direction = new Float32Array(ray.direction.toArray());
      hits = await this.xrSession.requestHitTest(
        origin,
        direction,
        this.frameOfRef
      );
    }

    if (hits && hits.length > 0 && this.hitTestFailed === 0) {
      let model = new THREE.Matrix4();
      model.fromArray(hits[0].hitMatrix);
      model.decompose(tempPos, tempQuat, tempScale);
      this.hitMovePoint.copy(tempPos);
    } else {
      this.hitTestFailed++;
      // this.mouse.x = this.mouse.x - this.hitOffset.x;
      // this.mouse.y = this.mouse.y - this.hitOffset.y;
      this.raycaster.setFromCamera(this.mouse, this.camera);
      let result = this.raycaster.intersectObjects([this.plane], true);
      if (result && result[0]) {
        this.hitMovePoint.copy(result[0].point);
      }
    }
  }

  raycast = () => {
    this.raycaster.setFromCamera(this.mouse, this.camera);
    let result = this.raycaster.intersectObjects([this.box], false);
    if (result && result[0]) {
      return result[0].point;
    }
  };

  onDown = () => {
    let point = this.raycast();
    if (point) {
      this.hit = true;
      this.dispatchEvent({
        type: 'down',
        object: this,
      });
      switch (this.STATE) {
        case STATE.MOVE:
          if (this.translate) {
            this.onDownPan(point);
            this.dispatchEvent({
              type: 'onDownPan',
              object: this,
            });
          }
          break;

        case STATE.ROTATE:
          if (this.rotate) {
            this.onDownRotate(point);
            this.dispatchEvent({
              type: 'onDownRotate',
              object: this,
            });
          }
          break;
      }
    }
  };

  async onDownPan(point) {
    this.updateHitOffset();
    await this.updateHit(point);
  }

  onDownRotate = point => {
    this.hitRotatePoint.copy(point);
  };

  onDrag = () => {
    let point = this.raycast();
    this.dispatchEvent({
      type: 'drag',
      object: this,
    });
    switch (this.STATE) {
      case STATE.MOVE:
        if (this.translate) {
          this.onDragPan(point);
          this.dispatchEvent({
            type: 'onDragPan',
            object: this,
          });
        }
        break;

      case STATE.ROTATE:
        if (this.rotate) {
          this.onDragRotate(point);
          this.dispatchEvent({
            type: 'onDragRotate',
            object: this,
          });
        }
        break;

      case STATE.SCALE:
        if (this.scale) {
          let scaleMethod;
          if (isMobile()) {
            scaleMethod = this.onTouchDragScale;
          } else {
            scaleMethod = this.onMouseDragScale;
          }
          scaleMethod();
          this.dispatchEvent({
            type: 'onDragScale',
            object: this,
          });
        }
        break;
    }
  };

  async onDragPan(point) {
    this.hitMovePointPrev.copy(this.hitMovePoint);
    await this.updateHit(point);
    tempPos.subVectors(this.hitMovePoint, this.hitMovePointPrev);
    tempPos.divideScalar(1.0 / this.translationSensitivity);
    this.model.position.set(
      this.model.position.x + tempPos.x,
      this.model.position.y + tempPos.y,
      this.model.position.z + tempPos.z
    );
    this.model.updateMatrixWorld(true);
  }

  onDragRotate = point => {
    if (point) {
      this.hitRotatePointPrev.copy(this.hitRotatePoint);
      this.hitRotatePoint.copy(point);
      this.v0.subVectors(this.origin, this.hitRotatePointPrev);
      this.v0.y = 0.0;
      this.v0.normalize();
      this.v1.subVectors(this.origin, this.hitRotatePoint);
      this.v1.y = 0.0;
      this.v1.normalize();
    }

    let angle = this.v0.angleTo(this.v1);
    if (
      Math.abs(this.touchAngleDelta) >
      this.touchMovementDistanceDelta * this.rotationSensitivity
    ) {
      tempAxis.set(0, 1, 0);
      this.quat.setFromAxisAngle(tempAxis, this.touchAngleDelta);
      this.originObject.applyQuaternion(this.quat);
      this.model.applyQuaternion(this.quat);
    } else if (angle >= 0.00001) {
      tempAxis.crossVectors(this.v0, this.v1);
      tempAxis.normalize();
      this.quat.setFromAxisAngle(
        tempAxis,
        this.touchMovementDistanceDelta * this.rotationSensitivity
      );
      this.originObject.applyQuaternion(this.quat);
      this.model.applyQuaternion(this.quat);
    }
  };

  onMouseDragScale = () => {
    let delta = this.mouse.y - this.mousePrev.y;
    this._scale(this.model.scale.x + delta);
  };

  onTouchDragScale = () => {
    this._scale(this.model.scale.x + this.touchSeparationDistanceDelta);
  };

  _scale = value => {
    let scale = Math.min(value, this.maxScale);
    scale = Math.max(scale, this.minScale);
    this.model.scale.set(scale, scale, scale);
    this.model.updateMatrixWorld(true);
    this.originObject.scale.set(scale, scale, scale);
    this.originObject.updateMatrixWorld(true);
  };

  onUp = () => {
    if (this.enabled) {
      if (this.down && this.hit) {
        this.dispatchEvent({
          type: 'up',
          object: this,
        });
      }
    }

    this.STATE = STATE.UNKNOWN;
    this.hitTestFailed = 0;
    this.down = false;
    this.hit = false;
  };

  mousedown = event => {
    if (this.enabled) {
      this.down = true;
      this.updateMouse(event.clientX, event.clientY);
      if (event.buttons == 2 || event.button == 2) {
        this.STATE = STATE.ROTATE;
      } else if (event.ctrlKey == true) {
        this.STATE = STATE.SCALE;
      } else {
        this.STATE = STATE.MOVE;
      }
      this.onDown();
    }
  };

  mousemove = event => {
    if (this.enabled) {
      if (this.down && this.hit) {
        this.updateMouse(event.clientX, event.clientY);
        this.onDrag();
      }
    }
  };

  mouseup = event => {
    if (this.enabled) {
      this.onUp();
    }
  };

  _touchStart = event => {
    let touches = event.touches;
    let keys = Object.keys(touches);
    keys.forEach(key => {
      let touch = touches[key];
      let id = touch.identifier;
      this.touchesHash[id] = createTouch();
      let touchObject = this.touchesHash[id];
      let x = touch.clientX / window.innerWidth;
      let y = 1.0 - touch.clientY / window.innerHeight;
      touchObject.current.set(x, y);
      touchObject.previous.copy(touchObject.current);
      touchObject.currentTouch = touch;
      touchObject.previousTouch = touch;
    });
  };

  touchStart = event => {
    if (this.enabled) {
      this._touchStart(event);

      let t = event.touches;
      let len = t.length;
      if (len) {
        this.down = true;
      }
      if (len === 1) {
        this.STATE = STATE.MOVE;
        this.updateMouse(t[0].clientX, t[0].clientY);
      } else {
        // this.STATE = STATE.UNKNOWN;
      }

      this.onDown();
    }
  };

  _touchMove = event => {
    let touches = event.touches;
    let keys = Object.keys(touches);
    keys.forEach(key => {
      let touch = touches[key];
      let id = touch.identifier;
      if (!(id in this.touchesHash)) {
        this.touchesHash[id] = createTouch();
      }
      let touchObject = this.touchesHash[id];
      if (touchObject) {
        touchObject.previous.copy(touchObject.current);
        let x = touch.clientX / window.innerWidth;
        let y = 1.0 - touch.clientY / window.innerHeight;
        touchObject.current.set(x, y);
        touchObject.previousTouch = touchObject.currentTouch;
        touchObject.currentTouch = touch;
      }
    });
  };

  touchMove = event => {
    if (this.enabled) {
      event.preventDefault();
      this._touchMove(event);

      let keys = Object.keys(this.touchesHash);
      let len = keys.length;

      if (this.down && this.hit && len) {
        this.down = true;
        if (len === 1 && this.STATE === STATE.MOVE) {
          this.STATE = STATE.MOVE;
          let touchObject = this.touchesHash[keys[0]];
          if (touchObject) {
            let touch = touchObject.currentTouch;
            this.updateMouse(touch.clientX, touch.clientY);
            this.onDrag();
          }
        } else if (len === 2) {
          let touchObject0 = this.touchesHash[keys[0]];
          let touchObject1 = this.touchesHash[keys[1]];

          let touch0 = touchObject0.currentTouch;
          let touch1 = touchObject1.currentTouch;

          this.updateMouse(
            (touch0.clientX + touch1.clientX) * 0.5,
            (touch0.clientY + touch1.clientY) * 0.5
          );

          tempCurrentTouchCenter.copy(touchObject0.current);
          tempCurrentTouchCenter.add(touchObject1.current);
          tempCurrentTouchCenter.multiplyScalar(0.5);

          tempPreviousTouchCenter.copy(touchObject0.previous);
          tempPreviousTouchCenter.add(touchObject1.previous);
          tempPreviousTouchCenter.multiplyScalar(0.5);

          let deltaMovementDistance = tempCurrentTouchCenter.distanceTo(
            tempPreviousTouchCenter
          );

          let currentSeparationDistance = touchObject0.current.distanceTo(
            touchObject1.current
          );

          let previousSeparationDistance = touchObject0.previous.distanceTo(
            touchObject1.previous
          );

          let deltaSeparationDistance =
            currentSeparationDistance - previousSeparationDistance;

          tempCurrentTouchVector.subVectors(
            touchObject0.current,
            touchObject1.current
          );
          tempPreviousTouchVector.subVectors(
            touchObject0.previous,
            touchObject1.previous
          );

          let deltaAngle =
            tempCurrentTouchVector.angle() - tempPreviousTouchVector.angle();

          this.touchSeparationDistanceDelta = deltaSeparationDistance;
          this.touchMovementDistanceDelta = deltaMovementDistance;
          this.touchAngleDelta = deltaAngle;

          if (this.rotate && this.scale) {
            let newState = Math.abs(deltaSeparationDistance) >
              deltaMovementDistance
              ? STATE.SCALE
              : STATE.ROTATE;

            let oldState = this.STATE;
            this.STATE = newState;
            if (newState === STATE.ROTATE && oldState !== STATE.ROTATE) {
              this.onDown();
            }
          } else if (this.rotate) {
            if (this.STATE != STATE.ROTATE) {
              this.STATE = STATE.ROTATE;
              this.onDown();
            }
            this.STATE = STATE.ROTATE;
          } else if (this.scale) {
            this.STATE = STATE.SCALE;
          } else {
            this.STATE = STATE.MOVE;
          }

          this.onDrag();
        }
      }
    }
  };

  touchEnd = event => {
    if (this.enabled) {
      let touches = event.changedTouches;
      let keys = Object.keys(touches);
      keys.forEach(key => {
        let touch = touches[key];
        let id = touch.identifier;
        let touchObject = this.touchesHash[id];
        this.touchesHash[id] = undefined;
        delete this.touchesHash[id];
      });
      this.onUp();
    }
  };
}



// WEBPACK FOOTER //
// ./src/ar/ARControls.js